/**
 * What Argus draws on a chart the rule's author chose (T3,
 * `docs/plans/argus.md`): the episodes and the rule's own numbers, on top of a
 * chart drawn exactly as Explore draws it.
 *
 * Phase 1 marks two plot types, because only they can say something true
 * without a change to their renderers:
 *
 * - **Line**: a band per episode, a dashed line per number whose series is on
 *   one of the chart's axes.
 * - **Scatter**: the points read inside an episode in Argus colour and the
 *   rest washed back, and the zone where the rule fires shaded - on x when the
 *   rule's series is the chart's x, as a dashed line when it is a y.
 *
 * Every other type is drawn without marks, and the page says so
 * (`MARKS_NOTE`). Nothing here evaluates (rule 3): the bands are the watcher's
 * episodes, the numbers the ones stored in the rule.
 */

import type uPlot from 'uplot';
import type { OverlayShape } from '../../state/chart-overlays.ts';
import type { ArgusBacktestChart } from '../../api/types.ts';
import { getCssVar } from '../plot/render/chart-dom.ts';
import { ARGUS_OVERLAY_COLOR } from './argus-overlays.ts';

/** An episode on the chart's clock: epoch seconds, end exclusive. */
export interface EpisodeSpan {
  from: number;
  to: number;
}

export type RuleLine = ArgusBacktestChart['lines'][number];

/** The plot types Argus can mark today. */
export function marksOn(plotType: string): boolean {
  return plotType === 'line' || plotType === 'scatter';
}

export const MARKS_NOTE = 'This chart type cannot show the episode yet. It is drawn for the window above.';

const label = (line: RuleLine): string => `${line.direction} ${line.value.toLocaleString()}`;

/** A line chart's marks. Its right axis is `y2` (`render-line.ts`). */
export function lineShapes(
  lines: readonly RuleLine[],
  episodes: readonly EpisodeSpan[],
  left: readonly string[],
  right: readonly string[],
): OverlayShape[] {
  const shapes: OverlayShape[] = episodes.map((e) => ({ kind: 'band', from: e.from, to: e.to, color: ARGUS_OVERLAY_COLOR }));
  for (const line of lines) {
    const scale = left.includes(line.series) ? 'y' : right.includes(line.series) ? 'y2' : null;
    if (scale) shapes.push({ kind: 'line', value: line.value, scale, label: label(line), color: ARGUS_OVERLAY_COLOR });
  }
  return shapes;
}

/**
 * A scatter's numbers. On x, the side where the rule fires, shaded to the
 * edge; on a y, a dashed line. The right axis is `yR` (`render-scatter.ts`).
 */
export function scatterShapes(
  lines: readonly RuleLine[],
  x: string,
  left: readonly string[],
  right: readonly string[],
): OverlayShape[] {
  const shapes: OverlayShape[] = [];
  for (const line of lines) {
    if (line.series === x) {
      // Far past any reading: the drawer clamps a band to the plot.
      const edge = line.direction === 'above' ? 1e15 : -1e15;
      shapes.push({ kind: 'band', from: line.value, to: edge, label: `fires ${label(line)}`, color: ARGUS_OVERLAY_COLOR });
      continue;
    }
    const scale = left.includes(line.series) ? 'y' : right.includes(line.series) ? 'yR' : null;
    if (scale) shapes.push({ kind: 'line', value: line.value, scale, label: label(line), color: ARGUS_OVERLAY_COLOR });
  }
  return shapes;
}

/** Which readings fall inside an episode, by their times (epoch seconds). */
export function insideEpisodes(times: readonly (number | null | undefined)[], episodes: readonly EpisodeSpan[]): boolean[] {
  const spans = [...episodes].sort((a, b) => a.from - b.from);
  return times.map((t) => typeof t === 'number' && Number.isFinite(t) && spans.some((s) => t >= s.from && t < s.to));
}

/**
 * Wash the scatter back, then draw the readings inside an episode on top in
 * Argus colour. A draw hook, after the chart has painted its own points.
 */
export function drawEpisodePoints(u: uPlot, inside: readonly boolean[], scales: readonly string[]): void {
  if (!inside.some(Boolean)) return;
  const ctx = u.ctx;
  const { left, top, width, height } = u.bbox;
  const d = window.devicePixelRatio || 1;
  ctx.save();
  ctx.globalAlpha = 0.6;
  ctx.fillStyle = getCssVar('--surface') || '#ffffff';
  ctx.fillRect(left, top, width, height);
  ctx.globalAlpha = 1;
  ctx.fillStyle = getCssVar('--argus') || '#217680';
  const xs = u.data[0] as ArrayLike<number | null>;
  for (let s = 1; s < u.data.length; s++) {
    const scale = scales[s - 1];
    if (!scale || !(scale in u.scales)) continue;
    const ys = u.data[s] as ArrayLike<number | null>;
    for (let i = 0; i < inside.length; i++) {
      const xv = xs[i];
      const yv = ys[i];
      if (!inside[i] || xv == null || yv == null) continue;
      const px = u.valToPos(xv, 'x', true);
      const py = u.valToPos(yv, scale, true);
      if (px < left || px > left + width || py < top || py > top + height) continue;
      ctx.beginPath();
      ctx.arc(px, py, 2.6 * d, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}
